import { DriftCorrector } from './drift-corrector';
import type {
  ClockInfo,
  DriftDirection,
  DriftState,
  HeartbeatBroadcast,
  SyncResult,
} from './types';
import type { VisibilityManager } from './visibility-manager';

interface FollowerSyncOptions {
  clock: ClockInfo;
  getLocalTimeMs: () => number;
  now?: () => number;
  visibilityManager?: VisibilityManager;
  onForegroundRejoin?: (sessionId: string) => void;
  sessionId?: () => string | null;
}

export class FollowerSync {
  private corrector = new DriftCorrector();
  private clock: ClockInfo;
  private getLocalTimeMs: () => number;
  private now: () => number;
  private _lastSeq = -1;
  private _visibilityManager?: VisibilityManager;
  private _unsubForeground?: () => void;

  constructor(options: FollowerSyncOptions) {
    this.clock = options.clock;
    this.getLocalTimeMs = options.getLocalTimeMs;
    this.now = options.now ?? (() => Date.now());
    this._visibilityManager = options.visibilityManager;

    if (options.visibilityManager && options.onForegroundRejoin) {
      const { onForegroundRejoin, sessionId } = options;
      this._unsubForeground = options.visibilityManager.onForeground((durationMs) => {
        if (durationMs >= 1000) {
          const sid = sessionId?.();
          if (sid) onForegroundRejoin(sid);
        }
      });
    }
  }

  get state(): DriftState {
    return this.corrector.state;
  }

  get lastSeq(): number {
    return this._lastSeq;
  }

  processHeartbeat(heartbeat: HeartbeatBroadcast): SyncResult | null {
    if (this._visibilityManager && !this._visibilityManager.isVisible()) {
      return null;
    }
    if (heartbeat.seq <= this._lastSeq) {
      return null;
    }
    this._lastSeq = heartbeat.seq;

    const clockOffset = this.clock.getClockOffset();
    const networkDelay = this.clock.getRTT() / 2;
    const serverTimestampLocal = heartbeat.serverTimestamp - clockOffset;
    const elapsed = this.now() - serverTimestampLocal - networkDelay;
    const estimatedDriverTimeMs = heartbeat.timeMs + elapsed;

    const localTimeMs = this.getLocalTimeMs();
    const driftMs = Math.abs(localTimeMs - estimatedDriverTimeMs);
    const direction: DriftDirection = localTimeMs > estimatedDriverTimeMs ? 'ahead' : 'behind';

    const action = this.corrector.evaluate(driftMs, direction, estimatedDriverTimeMs);

    return {
      state: this.corrector.state,
      driftMs,
      direction,
      action,
      estimatedDriverTimeMs,
    };
  }

  onSeekApplied(): void {
    this.corrector.reset();
  }
}
